import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/components/dropdown-menu';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import type { MeVenue } from '@/modules/auth/me';
import { useVenueContext } from './VenueContext';
import { NAV_ITEMS } from './venueNav';
import { computeSwitchTarget } from './switchTarget';
import { writeLastVenueId } from './lastVenue';

/**
 * The Venue switcher at the top of the workspace sidebar (ADR 0015, PRD #72). It is a
 * dropdown over `me.venues`, but a plain label when the user can reach exactly one Venue
 * — the UI must not imply a choice that does not exist. Switching preserves the current
 * feature when it is permitted at the target (see {@link computeSwitchTarget}) and records the
 * chosen Venue as last-used so the next login lands there.
 */
export function VenueSwitcher() {
  const { t } = useTranslation('common');
  const { venues } = useAuth();
  const auth = useAuthorization();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { venueId } = useVenueContext();

  const currentName = venues.find((venue) => venue.id === venueId)?.name ?? venueId;

  if (venues.length <= 1) {
    return (
      <p data-testid="workspace-venue-name" className="truncate text-sm font-medium">
        {currentName}
      </p>
    );
  }

  const switchTo = (target: MeVenue) => {
    if (target.id === venueId) return;
    writeLastVenueId(target.id);
    navigate(
      computeSwitchTarget({
        pathname,
        currentVenueId: venueId,
        targetVenueId: target.id,
        navItems: NAV_ITEMS,
        canAtTarget: (permissions) =>
          auth.canAny(permissions, { kind: 'venue', venueId: target.id }),
      }),
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('workspace.switcher.label')}
        className="flex w-full items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
      >
        <span data-testid="workspace-venue-name" className="truncate text-sm font-medium">
          {currentName}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {venues.map((venue) => (
          <DropdownMenuItem key={venue.id} onSelect={() => switchTo(venue)}>
            {venue.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
